import { isTelehealthProduct } from "~/hec/types/product";
import type { C_ProductInfo, ProductItem } from "~/lib/app.types";

import type { UnifiedRow } from "./basket-rows-context";

// Discriminator that decides snap-card copy + which side-effect "Add Item
// Back" triggers on top of the shared basket `add`: "removed" = basket trash
// → no follow-up; "savedForLater" = SFL row → after basket add settles, chain
// SFL cleanup (`cleanupAfterMove` for auth, `guestRemove` for guest).
export type SnapSource = "removed" | "savedForLater";

export type SnapEntry = {
  lineItem: ProductItem;
  // Slot position in productItems at capture time. Orphan snaps (productItem
  // already gone from the basket) splice back in at this index instead of
  // popping to end-of-list.
  originalIndex: number;
  source: SnapSource;
  // Telehealth classification captured at removal time. Classification normally
  // reads the PRODUCT (`telehealthProductIdSet`), but once the line leaves the
  // basket its product drops out of `products`, so a removed telehealth row can
  // no longer be classified that way. Snapshotting it here lets the removed row
  // keep its telehealth-first slot instead of being regrouped as core and
  // dropping to the bottom of the list (ET-204). Optional so fixtures and any
  // hand-built snap read as core; `captureSnapEntry` always sets it.
  isTelehealth?: boolean;
};

// Pure: build the SnapEntry that `BasketProvider` stores when a line is trashed
// or saved for later. This is the CAPTURE seam, and both of the things it does
// have to happen AT THE CLICK, which is why it's worth isolating:
//
//   - the whole `lineItem` is kept verbatim, so line-level data rides along.
//     ET-204 depends on this: `c_th_questionnaire_response_id` (the QRID) must
//     survive the removal for "Add Item Back" to re-attach the intake (ET-209 /
//     ET-287, `shouldRestampAddBackQrid`), and a capture that
//     projected a narrower shape would drop it silently.
//   - `isTelehealth` is derived from `products` — the basket's product list as of
//     THIS moment. After SCAPI confirms the remove the telehealth product leaves
//     `products`, so the removed row could no longer be classified at all; the
//     flag is the only thing keeping its telehealth-first slot (see
//     `isTelehealthRow`). Reading it from the line item, or after the settle,
//     misclassifies the row.
//
// `originalIndex` < 0 means the line was already gone from productItems (orphan
// capture) — fall back to `fallbackIndex` so the card still splices in
// deterministically instead of popping to end-of-list.
export function captureSnapEntry({
  lineItem,
  source,
  originalIndex,
  fallbackIndex,
  products,
}: {
  lineItem: ProductItem;
  source: SnapSource;
  originalIndex: number;
  fallbackIndex: number;
  products: readonly C_ProductInfo[] | undefined;
}): SnapEntry {
  const productId = lineItem.productId;
  return {
    lineItem,
    originalIndex: originalIndex < 0 ? fallbackIndex : originalIndex,
    source,
    isTelehealth:
      productId != null && telehealthProductIdSet(products).has(productId),
  };
}

// Pure: returns the set of snap itemIds that should be dropped because the
// same product has returned to the basket via an external path
// (SavedForLaterSection "Move to cart", PDP ATC, favorites move, cross-tab,
// etc.).
//
// Drop condition: snap's original `itemId` is GONE from productItems AND
// snap's `productId` is back in productItems (via a different itemId).
// The "still live" guard preserves the in-flight save/trash window where
// the snap is captured but the basket-remove leg hasn't settled yet
// (snap.itemId is still in productItems → skip drop).
//
// Symmetric for both snap sources ("removed" trash + "savedForLater" SFL).
export function reconcileStaleSnaps(
  productItems: readonly ProductItem[],
  snaps: ReadonlyMap<string, SnapEntry>,
): Set<string> {
  if (snaps.size === 0) return new Set();
  const liveItemIds = new Set<string>();
  const productIdsInBasket = new Set<string>();
  for (const item of productItems) {
    if (item.itemId) liveItemIds.add(item.itemId);
    if (item.productId) productIdsInBasket.add(item.productId);
  }
  const toDrop = new Set<string>();
  for (const [itemId, snap] of snaps) {
    const pid = snap.lineItem.productId;
    if (!pid) continue;
    if (liveItemIds.has(itemId)) continue;
    if (!productIdsInBasket.has(pid)) continue;
    toDrop.add(itemId);
  }
  return toDrop;
}

// Pure: render every productItem as kind:"normal"; render snap entries only
// when the productItem is gone from productItems (SCAPI confirmed the remove)
// and splice them at originalIndex to preserve slot position. Snaps whose
// itemId is still live in productItems are HIDDEN — the remove is still
// pending; the row stays visible with a loading spinner.
//
// Used inside BasketProvider's `useMemo` so `rows` is render-derived. See
// basket.tsx for the pending-snap lifecycle (capture on click, settle observer
// drops on failure, productItems-update reveals on success).
// Pure: stable-partition display rows so telehealth line items render first,
// above core products, while preserving the incoming order WITHIN each group
// (so core rows keep their newest-first ordering and telehealth rows keep
// theirs). Non-"normal" rows (removed snaps) are classified by `isTelehealth`
// like any other — a predicate returning false for them keeps them grouped
// with core. See cart.tsx `CartItemsBody` for the telehealth-first requirement
// (XD-149 / ET-31: telehealth products always appear before core products).
export function partitionTelehealthFirst(
  rows: readonly UnifiedRow[],
  isTelehealth: (row: UnifiedRow) => boolean,
): UnifiedRow[] {
  const telehealth: UnifiedRow[] = [];
  const core: UnifiedRow[] = [];
  for (const row of rows) {
    (isTelehealth(row) ? telehealth : core).push(row);
  }
  return [...telehealth, ...core];
}

// Pure: is this display row a telehealth row, for telehealth-first ordering?
//
// A "normal" row is classified from the live telehealth product-id set
// (`telehealthProductIdSet`) — classification reads the PRODUCT, not the line
// item. A "removed" snap row CAN'T be classified that way: once the line is
// gone its product has dropped out of `products`, so the set no longer contains
// it. Instead it uses the flag snapshotted onto the snap at removal time. This
// keeps a removed telehealth row in the telehealth group so its "Add Item Back"
// card holds the same slot rather than dropping to the bottom (ET-204) — the
// same in-place behaviour a removed core row already has.
export function isTelehealthRow(
  row: UnifiedRow,
  telehealthProductIds: ReadonlySet<string>,
): boolean {
  return row.kind === "normal"
    ? telehealthProductIds.has(row.lineItem.productId ?? "")
    : row.snap.isTelehealth === true;
}

// Pure: what should a "normal" row's quantity slot show — an interactive
// +/- stepper, or a non-interactive marker? (ET-243.)
//
// A telehealth line's quantity only ever changes via the plan-selection
// screen (picking a new plan/frequency), never an in-cart +/-. The mini-cart
// had no telehealth-aware row (unlike cart.tsx's `TelehealthCartRow`, which
// already omits the stepper), so it exposed the generic stepper on a
// prescription line with no duplicate-submit guard behind it. `staticLabel`
// mirrors `TelehealthCartRow`'s own convention: omitted at quantity 1 (the
// common case, unchanged), shown as "N ×" only when a merged add produced a
// quantity above 1.
//
// COM-902: a line whose product hasn't resolved is UNKNOWN, not core, so it is
// non-interactive too (see `lineQuantityEditable`). `products` is required so
// a caller can't silently fall back to the old fail-open read.
export function minicartQuantityDisplay(
  row: UnifiedRow,
  telehealthProductIds: ReadonlySet<string>,
  products: readonly C_ProductInfo[] | undefined,
): { interactive: boolean; staticLabel: string | null } {
  if (row.kind !== "normal") {
    return { interactive: true, staticLabel: null };
  }
  if (
    !isTelehealthRow(row, telehealthProductIds) &&
    lineQuantityEditable(row.lineItem, products)
  ) {
    return { interactive: true, staticLabel: null };
  }
  const quantity = row.lineItem.quantity ?? 1;
  // The space before "×" is U+00A0 (non-breaking), matching
  // TelehealthCartRow's `&nbsp;&times;` — a plain space would let "2" and
  // "×" wrap onto separate lines in the mini-cart's narrower `qty` column.
  return {
    interactive: false,
    staticLabel: quantity > 1 ? `${quantity}\u00A0×` : null,
  };
}

// Pure: the ids of the basket's telehealth products. Classification comes from
// the PRODUCT's `c_ownerClass`, not the line item — the SFRA calculate hook
// that stamps `ownerClass` onto line items is not guaranteed to have run for a
// SCAPI basket, so the product is the reliable source.
//
// Shared so the cart's two telehealth-aware surfaces (the telehealth-first row
// ordering and the "Due today:" total label) can't disagree about what counts
// as a telehealth cart.
export function telehealthProductIdSet(
  products: readonly C_ProductInfo[] | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const product of products ?? []) {
    if (product.id && isTelehealthProduct(product)) ids.add(product.id);
  }
  return ids;
}

// Pure: does the basket hold at least one telehealth line item? ET-202 —
// telehealth carts relabel the grand total "Due today:", because a telehealth
// plan bills again on its own cadence. Reads a line whose product hasn't
// resolved as core — right for a label; a gate that must not fail open wants
// `basketTelehealthState`.
export function hasTelehealthLineItem(
  productItems: readonly ProductItem[] | undefined,
  products: readonly C_ProductInfo[] | undefined,
): boolean {
  const telehealthIds = telehealthProductIdSet(products);
  if (telehealthIds.size === 0) return false;
  return (productItems ?? []).some(
    item => item.productId != null && telehealthIds.has(item.productId),
  );
}

// Pure: `hasTelehealthLineItem`, but `null` while any line's product is still
// unresolved (ET-393). `hasTelehealthLineItem` reads an unresolved line as core,
// which is fine for a label but not for a payment gate: products can lag the
// basket (a line added by an action renders against the previous products), so
// a telehealth cart would briefly look core-only and offer Apple Pay. Lines
// with no productId are skipped — there's nothing to classify, and treating
// them as unresolved would leave the cart unclassifiable for good. A product
// SCAPI never returns (offline, removed from the catalog) does the same, by
// design: the gate stays shut rather than guess.
export function basketTelehealthState(
  productItems: readonly ProductItem[] | undefined,
  products: readonly C_ProductInfo[] | undefined,
): boolean | null {
  const unresolved = (productItems ?? []).some(
    item => !!item.productId && !lineProductResolved(item.productId, products),
  );
  if (unresolved) return null;
  return hasTelehealthLineItem(productItems, products);
}

// Pure: which saved-for-later product ids may be rendered (ET-212).
//
// Telehealth items are withheld. Hiding the "Save for later" CTA stops NEW
// saves, but a telehealth item saved before ET-212 is still sitting in
// `c_saveLaterProducts` (or guest localStorage) with `inventory.orderable:
// true` — so it would render a live "Move to cart" and re-add a plan whose
// intake answers are now stale. That is the exact clinical failure the ticket
// exists to prevent, so the list is filtered on read too.
//
// Deliberately NON-destructive: the stored id is left alone, it just stops
// being offered. Purging it would mutate customer data as a side effect of a
// page load, and SFL mechanics / intake expiry are a deferred fast-follow that
// may want the id.
//
// FAILS OPEN. Only a positively-identified `Telehealth` product is dropped.
// `products` can legitimately be empty — `getSaveForLater` fail-softs to `[]`
// and the SFL resource hydrates in the background — and an unclassifiable id
// must stay VISIBLE, or one failed product fetch would blank a shopper's whole
// saved list. Same reason `telehealthProductIdSet` is the only classifier here:
// an id absent from `products` yields no set entry and is therefore kept.
//
// Order is preserved: the SFL list renders in stored order, so filtering must
// not reshuffle the survivors.
export function visibleSavedForLaterIds(
  productIds: readonly string[] | undefined,
  products: readonly C_ProductInfo[] | undefined,
): string[] {
  const telehealthIds = telehealthProductIdSet(products);
  // `filter` always allocates, so this is a copy by construction — callers can
  // never mutate the stored list through the return value.
  return (productIds ?? []).filter(id => !telehealthIds.has(id));
}

// Pure: has the product that would CLASSIFY this line resolved? (ET-212.)
//
// `CartItemsBody`'s telehealth dispatch reads the same `products` list and
// fails open — a telehealth line whose product is missing falls to the core
// branch and would render a working "Save for later", violating AC1. Rather
// than classify from the line item (CLAUDE.md rule #3: the SFRA calculate hook
// that stamps `ownerClass` onto a PLI doesn't run for SCAPI baskets), the cart
// withholds the control until the classifying product is present.
//
// Matches on `lineItem.productId` vs `product.id` — the identical pairing
// `telehealthProductIdSet` and `hasTelehealthLineItem` use, so this gate can
// never disagree with the classifier it protects.
export function lineProductResolved(
  productId: string | undefined | null,
  products: readonly C_ProductInfo[] | undefined,
): boolean {
  if (!productId) return false;
  return (products ?? []).some(product => product.id === productId);
}

// Pure: may this line offer an in-cart quantity change? (COM-902.)
//
// The quantity stepper is only safe on a line we KNOW is core. A telehealth
// line's quantity changes via plan selection only (ET-243), and the update
// action has no telehealth guard behind the stepper — so a telehealth line
// whose product is missing from `products` must not fall through to the core
// stepper. Before COM-902 that window was narrow (an action-added line against
// `lastProductsRef`); a partial basket payload (products enrichment timed out)
// now puts the real basket on screen with some or all products missing — on a
// cold load `lastProductsRef` is empty, so EVERY line is unresolved — until the
// next mutation or navigation.
//
// Lines with no productId stay editable, matching `basketTelehealthState`:
// there is nothing to classify, and gating them would lock them for good. The
// cost to a CORE line is the same honest trade as ET-212's Save-for-later
// gate: a line added by an action briefly shows its quantity without the
// stepper until the trailing reload brings its product.
export function lineQuantityEditable(
  lineItem: Pick<ProductItem, "productId">,
  products: readonly C_ProductInfo[] | undefined,
): boolean {
  if (!lineItem.productId) return true;
  return lineProductResolved(lineItem.productId, products);
}

export function deriveBasketRows(
  productItems: readonly ProductItem[],
  snaps: ReadonlyMap<string, SnapEntry>,
  restoredPositions: ReadonlyMap<string, number> = new Map(),
  restoringReplacements: ReadonlyMap<string, string> = new Map(),
): UnifiedRow[] {
  const liveIds = new Set<string>();
  let out: UnifiedRow[] = [];
  for (const item of productItems) {
    if (item.itemId) liveIds.add(item.itemId);
    out.push({ kind: "normal", lineItem: item });
  }
  for (const [itemId, originalIndex] of [...restoredPositions.entries()].sort(
    (a, b) => a[1] - b[1],
  )) {
    const currentIndex = out.findIndex(
      row => row.kind === "normal" && row.lineItem.itemId === itemId,
    );
    if (currentIndex < 0) continue;
    const [row] = out.splice(currentIndex, 1);
    out.splice(Math.min(originalIndex, out.length), 0, row);
  }
  const liveRestoredSnapIds = new Set<string>();
  for (const [snapItemId, replacementItemId] of restoringReplacements) {
    if (liveIds.has(replacementItemId)) liveRestoredSnapIds.add(snapItemId);
  }
  const orphans = [...snaps.entries()]
    .filter(([id]) => !liveIds.has(id) && !liveRestoredSnapIds.has(id))
    .sort((a, b) => a[1].originalIndex - b[1].originalIndex);
  let result = out;
  for (const [itemId, s] of orphans) {
    const at = Math.min(s.originalIndex, result.length);
    result = [
      ...result.slice(0, at),
      { kind: "removed", itemId, snap: s },
      ...result.slice(at),
    ];
  }
  return result;
}
